import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { GridColDef, GridColumnVisibilityModel, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { useIntl } from 'react-intl';
import LanguageUtils from '../../../utils/LanguageUtils';
import TableUtils from '../../../utils/tableUtils';
import UrlConstants from '../../../constants/UrlConstants';
import CustomStringLink from '../../common/customStringLink';
import HelptextConstantsEnum from '../../../constants/helptextConstantsEnum';
import DataGridComponent from '../../common/dataGrid';
import NullableFormattedDate from '../../common/nullableFormattedDate';
import TruncatedStringTooltip from '../../common/truncatedStringTooltip';
import NumericFormat from 'react-number-format';

interface OrdersNotYetInvoicedListProps {
    classes?: any;
    checkSelection?: any;
    isLoading?: boolean;
    multipleSelection?: boolean;
    componentsProps?: any;
    onPageChange?: any;
    rows?: any;
    applyRowFn?: any;
    hiddenColumns?: Array<string>;
    onSortModelChange?: any;
    columnVisibilityModel?: GridColumnVisibilityModel;
    onColumnVisibilityModelChange?: any;
    setCurrentComponentId?: any;
    disableLinks?: boolean;
}

const messages = {
    customerName: LanguageUtils.createMessage('Customer Name'),
    departureYear: LanguageUtils.createMessage('Departure Year'),
    departureMonth: LanguageUtils.createMessage('Departure Month'),
    ousStatusName: LanguageUtils.createMessage('Ous Status'),
    currencyCode: LanguageUtils.createMessage('Currency'),
    amount: LanguageUtils.createMessage('Amount'),
    revenue: LanguageUtils.createMessage('Revenue'),
    nextInvoiceDate: LanguageUtils.createMessage('Next Invoice Date'),
    serviceGroupName: LanguageUtils.createMessage('Service group'),
    totalRevenue: LanguageUtils.createMessage('Total revenue')
};

const OrdersNotYetInvoicedFullReportList = forwardRef((props: OrdersNotYetInvoicedListProps, ref: any): JSX.Element => {
    const { componentsProps, isLoading, checkSelection, onPageChange, rows, hiddenColumns, multipleSelection, onSortModelChange,
        columnVisibilityModel, onColumnVisibilityModelChange, setCurrentComponentId } = props;

    const defaultFn = (cellValues: GridRenderCellParams | GridValueGetterParams) => cellValues.row;

    const applyRowFn = props.applyRowFn || defaultFn;

    const dataGridRef = useRef();

    useImperativeHandle(ref, () => ({
        resetDataGridPage() {
            if(dataGridRef !== null && dataGridRef !== undefined && dataGridRef.current !== null && dataGridRef.current !== undefined) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                dataGridRef.current.resetPage();
            }
        }
    }));

    useEffect(() => {
        if(setCurrentComponentId !== undefined) {
            setCurrentComponentId(HelptextConstantsEnum.REPORT_ORDERS_NOT_INVOICES);
        }
    }, []);
  
    function generateColumns(): GridColDef[] {
        const intl = useIntl();

        return [
            {
                field: 'customerNoName',
                headerName: intl.formatMessage(messages.customerName),
                flex: 1.5,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (cellValues: GridRenderCellParams) => {
                    return <CustomStringLink value={applyRowFn(cellValues)?.customerNoName} link={`/#/${UrlConstants.CUSTOMERS}/${applyRowFn(cellValues)?.customer?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('customerNoName', hiddenColumns)
            },
            {
                field: 'departureYear',
                headerName: `${intl.formatMessage(messages.departureYear)}`,
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (cellValues: GridRenderCellParams) => {
                    return <TruncatedStringTooltip value={applyRowFn(cellValues)?.departureYear} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('code', hiddenColumns)
            },
            {
                field: 'departureMonth',
                headerName: intl.formatMessage(messages.departureMonth),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (params: any) => {
                    return <TruncatedStringTooltip value={applyRowFn(params)?.departureMonth} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('fromDate', hiddenColumns)
            },
            {
                field: 'ousStatusName',
                headerName: intl.formatMessage(messages.ousStatusName),
                flex: 1,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (params: any) => {
                    return <TruncatedStringTooltip value={applyRowFn(params)?.ousStatusName} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('ousStatusName', hiddenColumns)
            },
            {
                field: 'serviceGroupNameNo',
                headerName: intl.formatMessage(messages.serviceGroupName),
                flex: 1,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (params: any) => {
                    return <TruncatedStringTooltip value={applyRowFn(params)?.serviceGroupNameNo} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('serviceGroupNameNo', hiddenColumns)
            },
            {
                field: 'currencyCode',
                headerName: intl.formatMessage(messages.currencyCode),
                flex: 0.5,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (params: any) => {
                    return <TruncatedStringTooltip value={applyRowFn(params)?.currencyCode} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('currencyCode', hiddenColumns)
            },
            {
                field: 'amount',
                headerName: intl.formatMessage(messages.amount),
                flex: 0.3,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (params: any) => {
                    return <TruncatedStringTooltip value={applyRowFn(params)?.amount} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('amount', hiddenColumns)
            },
            {
                field: 'revenue',
                headerName: intl.formatMessage(messages.revenue),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (params: any) => {
                    return <NumericFormat value={applyRowFn(params)?.revenue} displayType={'text'} thousandSeparator={true} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('revenue', hiddenColumns)
            },
            {
                field: 'nextInvoiceDate',
                headerName: intl.formatMessage(messages.nextInvoiceDate),
                flex: 0.8,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (params: any) => { return (<NullableFormattedDate value={params.row?.nextInvoiceDate} />); },
                hide: hiddenColumns && TableUtils.hideColumn('nextInvoiceDate', hiddenColumns)
            }
        ];
    }

    return (
        <DataGridComponent getRowId={(row) => row?.customerNoName + row?.amount + Math.random()} rows={rows} multipleSelection={multipleSelection} columns={generateColumns()}
            ref={dataGridRef} componentsProps={componentsProps} isLoading={isLoading}
            checkSelection={checkSelection} onPageChange={onPageChange} onSortModelChange={onSortModelChange}
            columnVisibilityModel={columnVisibilityModel} onColumnVisibilityModelChange={onColumnVisibilityModelChange} />
    );
});

export default OrdersNotYetInvoicedFullReportList;
